import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import PageErrorTemplate from 'pages/PageErrorTemplate';

const ErrorFallback = () => {
  return (
    <div
      className="container min-height margin-top-4 margin-bottom-4"
      role="alert"
    >
      <PageErrorTemplate title="DataError" desc="UnknownError" />
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    const { resetKeys } = this.props;
    if (!this.state.hasError || !resetKeys || !prevProps.resetKeys) return;

    const changed =
      resetKeys.length !== prevProps.resetKeys.length ||
      resetKeys.some((key, i) => !Object.is(key, prevProps.resetKeys[i]));

    if (changed) {
      this.reset();
    }
  }

  reset() {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  }

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.FallbackComponent ?? (() => null);
      return (
        <Fallback error={this.state.error} resetErrorBoundary={this.reset} />
      );
    }
    return this.props.children;
  }
}

const ErrorBoundaries = ({ navigate, resetKeys, children }) => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        navigate?.('/');
      }}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundary>
  );
};

export const ErrorBoundariesWithNavigate = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ErrorBoundaries
      navigate={navigate}
      resetKeys={[location.pathname]}
      {...props}
    />
  );
};

export default ErrorBoundaries;
